import React, { useMemo } from "react";
import "./OurVision.css";

import { useTranslation } from "react-i18next";
import { usePageContent } from "../../../../hooks/usePublicQueries";
import { resolveMediaUrl } from "../../../../utils/media";

const itemFieldRegex = /^item(\d+)([a-zA-Z0-9_]+)$/i;
const cardFieldRegex = /^card(\d+)([a-zA-Z0-9_]+)$/i;

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceVisionPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    return coalesceVisionPayload(tryParseJsonObject(raw) || {});
  }
  if (typeof raw !== "object") return null;

  const mergeVisionWrapper = (node, into) => {
    const wrap = node?.ourVision ?? node?.OurVision ?? node?.ourvision;
    if (!wrap || typeof wrap !== "object") return into;
    if (wrap.data && typeof wrap.data === "object" && !Array.isArray(wrap.data)) {
      return { ...into, ...wrap.data };
    }
    return { ...into, ...wrap };
  };

  let base = { ...raw };
  base = mergeVisionWrapper(raw, base);
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...base, ...raw.data };
    base = mergeVisionWrapper(raw.data, base);
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return mergeVisionWrapper(base, base);
};

const normalizeSectionSlug = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

const findPageSection = (root, slug) => {
  if (!root || typeof root !== "object") return undefined;
  const target = normalizeSectionSlug(slug);
  const hit = Object.keys(root).find((k) => normalizeSectionSlug(k) === target);
  return hit != null ? root[hit] : undefined;
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const hasMeaningfulVisionPayload = (c) => {
  if (!c || typeof c !== "object") return false;
  if (pickString(c.tag, c.sectionTag, c.title, c.heading, c.titleLine1)) return true;
  return Object.keys(c).some((k) => itemFieldRegex.test(k) || cardFieldRegex.test(k));
};

const getCardIconFromApi = (content, itemNumber) => {
  const suffix = String(itemNumber);
  const candidates = [
    content?.[`card${suffix}Icon`],
    content?.[`Card${suffix}Icon`],
    content?.[`card_${suffix}_icon`],
    content?.[`item${suffix}Icon`],
    content?.[`item${suffix}icon`],
    content?.[`item${suffix}Image`],
    content?.[`item${suffix}image`],
    content?.[`item${suffix}Media`],
    content?.[`item${suffix}media`],
    content?.[`item${suffix}MediaUrl`],
    content?.[`item${suffix}mediaUrl`],
  ];
  for (const candidate of candidates) {
    const resolved = resolveMediaUrl(candidate);
    if (resolved) return resolved;
  }
  return "";
};

const OurVision = ({ content }) => {
  const { i18n } = useTranslation();
  const shouldFetch = !(content && typeof content === "object");
  const { data: aboutPage } = usePageContent("about", i18n.language, { enabled: shouldFetch });

  const apiContent = useMemo(() => {
    if (!shouldFetch) return null;
    return findPageSection(aboutPage, "ourvision") ?? null;
  }, [aboutPage, shouldFetch]);

  const visionContent = useMemo(() => {
    const source = content ?? apiContent;
    const payload = coalesceVisionPayload(source);
    if (!hasMeaningfulVisionPayload(payload) || !payload) return null;
    return payload;
  }, [content, apiContent]);

  const cards = useMemo(() => {
    if (!visionContent) return [];
    const cardsMap = new Map();

    const ingestField = (index, fieldRaw, value) => {
      const field = String(fieldRaw || "").replace(/[_\s-]+/g, "").toLowerCase();
      if (!cardsMap.has(index)) cardsMap.set(index, {});
      cardsMap.get(index)[field] = value;
    };

    Object.entries(visionContent).forEach(([key, value]) => {
      const itemMatch = key.match(itemFieldRegex);
      if (itemMatch) {
        ingestField(Number(itemMatch[1]), itemMatch[2], value);
        return;
      }
      const cardMatch = key.match(cardFieldRegex);
      if (cardMatch) {
        ingestField(Number(cardMatch[1]), cardMatch[2], value);
      }
    });

    return Array.from(cardsMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([index, fields]) => {
        const icon =
          getCardIconFromApi(visionContent, index) ||
          resolveMediaUrl(fields.icon || fields.image || fields.media || fields.mediaurl) ||
          "";
        return {
          index,
          title: pickString(fields.title, fields.heading),
          description: pickString(fields.description, fields.text, fields.body),
          icon,
        };
      })
      .filter((item) => item.title || item.description || item.icon);
  }, [visionContent]);

  if (!visionContent) return null;

  const tag = pickString(visionContent.tag, visionContent.sectionTag);
  const title = pickString(
    visionContent.title,
    visionContent.heading,
    [visionContent.titleLine1, visionContent.titleLine2].filter(Boolean).join(" ").trim()
  );

  if (!tag && !title && !cards.length) return null;

  return (
    <section className="ourvision">
      {tag || title ? (
        <div className="ourvision-top">
          {tag ? <span className="ourvision-tag">{tag}</span> : null}
          {title ? <h2>{title}</h2> : null}
        </div>
      ) : null}

      {cards.length > 0 ? (
        <div className="ourvision-grid">
          {cards.map((card) => (
            <div className="ourvision-card" key={`ourvision-card-${card.index}`}>
              {card.icon ? (
                <img
                  src={card.icon}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : null}
              <div>
                {card.title ? <h3>{card.title}</h3> : null}
                {card.description ? <p>{card.description}</p> : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default OurVision;
